import { useMemo } from 'react';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { truncateString } from '../../utils/truncate-string';
import { ShowHideRootContext } from '../show-hide/context/show-hide-root.context';
import { ShowHideButton } from '../show-hide/show-hide-button';
import type { ShowHideRootContextInterface } from '../show-hide/types/show-hide-root.types';
import type { ShowHideWordCountPropsInterface } from './types/show-hide-word-count.types';

export const ShowHideWordCount = (props: ShowHideWordCountPropsInterface) => {
  const {
    isHidden,
    defaultHidden = true,
    onDisplayChange,
    renderButton,
    wordCount = 100,
    text,
  } = props;

  const [isHiddenState, setIsHiddenState] = useControllableValue<boolean | undefined>({
    controlledValue: isHidden,
    uncontrolledValue: defaultHidden,
    isControlled: 'isHidden' in props,
    isUncontrolled: 'defaultHidden' in props,
    onChange: () => onDisplayChange?.(),
    componentName: 'ShowHideWordCount',
    controlledPropName: 'isHidden',
    uncontrolledPropName: 'defaultHidden',
  });

  const button = useMemo(
    () =>
      typeof renderButton === 'function' ? (
        renderButton(props)
      ) : (
        <ShowHideButton>{isHiddenState ? 'See more' : 'See less'}</ShowHideButton>
      ),
    [renderButton, props, isHiddenState],
  );

  const content = useMemo(
    () => (isHiddenState ? truncateString(wordCount, text) : text),
    [isHiddenState, wordCount, text],
  );

  const contextValue = useMemo<ShowHideRootContextInterface>(
    () => ({
      appearance: 'inline',
      isHidden: isHiddenState,
      setIsHidden: setIsHiddenState,
    }),
    [isHiddenState, setIsHiddenState],
  );

  return (
    <ShowHideRootContext.Provider value={contextValue}>
      <>
        {content}
        {isHiddenState ? null : ` `}
        {button}
      </>
    </ShowHideRootContext.Provider>
  );
};

ShowHideWordCount.displayName = 'ShowHideWordCount';
